import { Check } from "lucide-react";
import { SectionHead, Provisional, Partners, CtaBand, useList } from "@/components/Blocks";
import { useSite } from "@/lib/i18n";
import { useSeo } from "@/lib/analytics";

export const PageHero = ({ eyebrow, title, sub, children }) => (
  <section className="relative overflow-hidden bg-navy noise">
    <div className="absolute inset-0 grid-bg" />
    <div className="wrap relative py-20 md:py-24">
      <p className="rise font-mono text-xs uppercase tracking-[0.25em] text-signal">{eyebrow}</p>
      <h1 className="rise mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white" style={{ animationDelay: ".08s" }}>{title}</h1>
      {sub && <p className="rise mt-5 max-w-2xl text-base md:text-lg text-slate-300" style={{ animationDelay: ".16s" }}>{sub}</p>}
      {children}
    </div>
  </section>
);

export default function About() {
  const { site } = useSite();
  const a = site.about || {};
  const team = useList("/public/team") || [];
  const partners = useList("/public/partners") || [];
  useSeo({ title: "Nosotros", description: a.who });
  return (
    <>
      <PageHero eyebrow="Nosotros" title="Comprendemos su operación antes de escribir una línea de código." sub={a.who} />
      <section className="wrap grid gap-12 py-16 md:py-24 lg:grid-cols-2">
        <div>
          <p className="eyebrow">Nuestra experiencia</p>
          <p className="mt-4 text-lg leading-relaxed text-slate-700" data-testid="about-experience">{a.experience}</p>
          <p className="eyebrow mt-12">Visión tecnológica</p>
          <p className="mt-4 leading-relaxed text-slate-600">{a.vision}</p>
          <p className="eyebrow mt-12">Compromiso con los clientes</p>
          <p className="mt-4 leading-relaxed text-slate-600">{a.commitment}</p>
        </div>
        <div className="card-cc bg-[color:var(--cc-bg)]">
          <p className="eyebrow">Especialización</p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {(a.specialization || []).map((s) => (
              <li key={s} className="flex items-center gap-2 text-sm font-medium text-slate-700"><Check className="h-4 w-4 text-brand" />{s}</li>
            ))}
          </ul>
        </div>
      </section>
      <section className="border-y border-slate-200 bg-[color:var(--cc-bg)] py-16 md:py-24">
        <div className="wrap">
          <SectionHead eyebrow="Metodología" title="Un proceso claro, de principio a evolución" />
          <ol className="grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 md:grid-cols-3" data-testid="methodology-steps">
            {(a.methodology || []).map((m, i) => (
              <li key={m} className="bg-white p-6 md:p-8">
                <span className="font-mono text-xs text-brand">PASO {String(i + 1).padStart(2, "0")}</span>
                <p className="mt-3 font-display text-lg font-semibold">{m}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      {partners.length > 0 && (
        <section className="wrap pt-16 md:pt-24" data-testid="about-partners">
          <SectionHead eyebrow="Alianzas" title="Alianzas estratégicas" />
          <Partners items={partners} />
        </section>
      )}
      <section className="wrap py-16 md:py-24">
        <SectionHead eyebrow="Liderazgo técnico" title="El equipo detrás de las soluciones" />
        {team.length ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((m) => (
              <div key={m.id} className="card-cc" data-testid={`team-${m.id}`}>
                {m.photo_url ? <img src={m.photo_url} alt={m.name} className="h-16 w-16 rounded-full object-cover" /> : <span className="grid h-16 w-16 place-items-center rounded-full bg-navy text-xl font-bold text-white">{m.name?.[0]}</span>}
                <p className="mt-4 font-display font-semibold">{m.name}</p>
                <p className="text-sm text-brand">{m.role}</p>
                <p className="mt-2 text-sm text-slate-600">{m.bio}</p>
              </div>
            ))}
          </div>
        ) : (
          <Provisional testid="team-placeholder" title="Perfiles del liderazgo técnico" text="Los perfiles profesionales se publicarán únicamente con la aprobación de cada persona." />
        )}
      </section>
      <CtaBand />
    </>
  );
}
